import React, { useState, useEffect, useContext } from "react";
import { Form, Button, message } from "antd";
import { TechCardModal } from "src/enitities/Modal";
import { TechCardsForm } from "src/enitities/Form";
import { EditOutlined } from "@ant-design/icons";
import { TechCardContext } from "src/shared/lib/hooks/context/getTechCardsContext";
import { techCardsAPI } from "src/shared/api/techCardsAPI";

export default function EditTechCard({ record, token }) {
  const [isOpen, setOpen] = useState(false);
  const [changeData, setChangeData] = useState(false);
  const [parent, setParent] = useState([]);
  const [form] = Form.useForm();
  const { refreshData } = useContext(TechCardContext);
  const [existingItems, setExistingItems] = useState([]);
  const [nomenclatureNames, setNomenclatureNames] = useState({});
  const [nomenclatureData, setNomenclatureData] = useState({});

  const prepareDataForAPI = (values) => {
    console.log("EditTechCard: Preparing data for API with form values:", JSON.stringify(values, null, 2));
    
    const preparedItems = (values.items || []).filter((item) => item.nomenclature_id !== null)
      .map((item) => {
        const waste_from_heat_processing = parseFloat(item.waste_from_heat_processing || 0.01);
        if (waste_from_heat_processing <= 0) {
          throw new Error(`waste_from_heat_processing для позиции ${item.nomenclature_id} должно быть больше 0`);
        }

        const waste_from_cold_processing = parseFloat(item.waste_from_cold_processing || 0.01);
        if (waste_from_cold_processing <= 0) {
          throw new Error(`waste_from_cold_processing для позиции ${item.nomenclature_id} должно быть больше 0`);
        }
        return {
          nomenclature_id: parseInt(item.nomenclature_id),
          type_of_processing: item.type_of_processing || "cutting",
          net_weight: parseFloat(item.net_weight || 0.1),
          waste_from_cold_processing,
          waste_from_heat_processing,
          quantity: parseInt(item.quantity || 1),
          gross_weight: parseFloat(item.gross_weight || 0.11),
          output: parseFloat(item.output || 0.1),
        };
      });

    const preparedData = {
      name: values.name || "",
      description: values.description || "",
      code: values.code || "",
      card_type: values.card_type || "reference",
      auto_produce: values.auto_produce || false,
      parent: values.parent ? parseInt(values.parent) : null,
      status: values.status ? "active" : "inactive",
      items: preparedItems,
    };
    
    console.log("EditTechCard: Prepared data:", JSON.stringify(preparedData, null, 2));
    return preparedData;
  };

  const handleSubmit = async (values) => {
    try {
      console.log("EditTechCard: handleSubmit started with values:", JSON.stringify(values, null, 2));
      
      const preparedData = prepareDataForAPI(values);
      console.log("EditTechCard: Sending update request with data:", JSON.stringify(preparedData, null, 2));
      const result = await techCardsAPI.update(token)(preparedData, record.id);
      console.log("EditTechCard: Server response:", JSON.stringify(result.data, null, 2));

      if (refreshData) {
        console.log("EditTechCard: Triggering refreshData");
        await refreshData();
      }

      setOpen(false);
      form.resetFields();
      message.success("Техкарта успешно обновлена");
      return result;
    } catch (error) {
      console.error("EditTechCard: Error in handleSubmit:", error);
      console.log("EditTechCard: Error response data:", error.response?.data);
      console.log("EditTechCard: Error request config:", error.config);
      
      if (error.response?.status === 307) {
        message.error("Ошибка: Сервер перенаправил запрос. Проверьте конфигурацию API.");
      } else if (error.response?.status === 422) {
        const errorData = error.response.data;
        if (errorData.detail) {
          errorData.detail.forEach((err) => {
            if (err.loc && err.msg) {
              const fieldName = err.loc[err.loc.length - 1];
              message.error(`Ошибка в поле "${fieldName}": ${err.msg}`);
            }
          });
        } else {
          message.error(`Ошибка валидации: ${JSON.stringify(errorData)}`);
        }
      } else if (error.response?.status === 500) {
        message.error("Ошибка на сервере (500). Обратитесь к администратору.");
        console.group("❌ 500 Internal Server Error Details");
        console.log("Response data:", error.response?.data);
        console.log("Request config:", error.config);
        console.groupEnd();
      } else {
        message.error("Ошибка при обновлении техкарты");
      }
      throw error;
    }
  };

  useEffect(() => {
    if (isOpen) {
      console.log("EditTechCard: Modal opened, initializing form with record:", JSON.stringify(record, null, 2));
      
      techCardsAPI
        .getItems({ limit: 500, token })
        .then((res) => {
          console.log("EditTechCard: API response for parent:", JSON.stringify(res.data, null, 2));
          const result = res.data.result || res.data || [];
          if (Array.isArray(result)) {
            const parentOptions = result
              .filter((item) => item.id !== record.id)
              .map((item) => ({
                value: item.id,
                label: item.name,
              }));
            setParent(parentOptions);
          } else {
            console.warn("EditTechCard: Unexpected API response format:", result);
            setParent([]);
          }
        })
        .catch((error) => {
          console.error("EditTechCard: Error fetching parent tech cards:", error);
          setParent([]);
        });

      techCardsAPI
        .getTechCardItems(token, record.id)
        .then((res) => {
          const items = res.data || [];
          setExistingItems(items);
          console.log("EditTechCard: Fetched items:", JSON.stringify(items, null, 2));

          const fetchNomenclatureNames = async () => {
            const names = {};
            const nomData = {};
            for (const [index, item] of items.entries()) {
              if (item.nomenclature_id) {
                try {
                  const response = await fetch(
                    `https://${process.env.REACT_APP_APP_URL}/api/v1/nomenclature/${item.nomenclature_id}/?token=${token}`
                  );
                  const data = await response.json();
                  names[index] = data.name || "Неизвестно";
                  // Сохраняем полные данные номенклатуры
                  nomData[data.id] = {
                    id: data.id,
                    name: data.name || "Неизвестно",
                    type: data.type || "",
                    tags: data.tags || [],
                    description_short: data.description_short || "",
                    description_long: data.description_long || "",
                    code: data.code || "",
                    unit_name: data.unit_name || "",
                    category_name: data.category_name || "",
                    manufacturer_name: data.manufacturer_name || "",
                    cashback_type: data.cashback_type || "",
                    cashback_percent: data.cashback_percent || 0,
                  };
                } catch (error) {
                  console.error(`Error fetching nomenclature ${item.nomenclature_id}:`, error);
                  names[index] = "Неизвестно";
                }
              }
            }
            setNomenclatureNames(names);
            setNomenclatureData(nomData);
          };

          fetchNomenclatureNames().then(() => {
            form.setFieldsValue({
              ...record,
              status: record.status === "active",
              items: items.map((item) => ({
                ...item,
                nomenclature_id: item.nomenclature_id,
                type_of_processing: item.type_of_processing || "cutting",
                net_weight: parseFloat(item.net_weight || 0.1),
                waste_from_cold_processing: parseFloat(item.waste_from_cold_processing || 0),
                waste_from_heat_processing: parseFloat(item.waste_from_heat_processing || 0.01),
                quantity: parseInt(item.quantity || 1),
                gross_weight: parseFloat(item.gross_weight || 0.11),
                output: parseFloat(item.output || 0.1),
              })),
              parent: record.parent || null,
            });
            console.log("EditTechCard: Form initialized with values:", JSON.stringify(form.getFieldsValue(), null, 2));
          });
        })
        .catch((error) => {
          console.error("EditTechCard: Error fetching tech card items:", error);
          setExistingItems([]);
          form.setFieldsValue({
            ...record,
            status: record.status === "active",
            items: (record.items || []).map((item) => ({
              ...item,
              nomenclature_id: item.nomenclature_id,
              type_of_processing: item.type_of_processing || "cutting",
              net_weight: parseFloat(item.net_weight || 0.1),
              waste_from_cold_processing: parseFloat(item.waste_from_cold_processing || 0),
              waste_from_heat_processing: parseFloat(item.waste_from_heat_processing || 0.01),
              quantity: parseInt(item.quantity || 1),
              gross_weight: parseFloat(item.gross_weight || 0.11),
              output: parseFloat(item.output || 0.1),
            })),
            parent: record.parent || null,
          });
        });
    }
  }, [isOpen, form, record, token]);

  return (
    <>
      <Button
        icon={<EditOutlined />}
        onClick={() => {
          console.log("EditTechCard: Edit button clicked");
          setOpen(true);
        }}
        style={{ marginRight: "5px" }}
      />
      <TechCardModal
        isOpen={isOpen}
        setOpen={setOpen}
        title="Редактировать тех. карту"
        width={1800}
        formContext={form}
        handleSubmit={handleSubmit}
        changeData={changeData}
        setChangeData={setChangeData}
        okText="Подтвердить"
        cancelText="Отмена"
        destroyOnClose
        maskClosable={false}
      >
        <TechCardsForm
          formContext={form}
          parent={parent}
          record={record}
          setChangeData={setChangeData}
          token={token}
          api={`https://${process.env.REACT_APP_APP_URL}/api/v1/`}
          nomenclatureNames={nomenclatureNames}
          existingItems={existingItems}
          nomenclatureData={nomenclatureData}
        />
      </TechCardModal>
    </>
  );
}